import React, { useEffect, useState } from 'react';
import { MobileInvoicePreviewContent } from './MobileInvoicePreview';
import { buildInvoicePreviewPayload } from '../utils/invoicePreview';

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

export default function PrintInvoicePage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const isPdfExport = params.get('pdfExport') === '1';
  // path is /print/invoice/:id — pull id off the pathname directly
  const id = window.location.pathname.split('/print/invoice/')[1];

  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !token) {
      setError('Missing invoice id or token');
      return;
    }
    (async () => {
      console.log('[PrintInvoicePage] fetching', `${BASE}/invoices/${id}`);
      try {
        const res = await fetch(`${BASE}/invoices/${id}/print-data`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('[PrintInvoicePage] fetch resolved, status:', res.status);
        if (!res.ok) throw new Error(`Failed to fetch invoice: ${res.status}`);
        const invoice = await res.json();
        console.log('[PrintInvoicePage] invoice parsed, id:', invoice.id);
        const payload: any = buildInvoicePreviewPayload(invoice);

        // ⚡ 100% Generic Merge: Pull ANY dynamic user-added columns from table.rows & raw line_items
        const tableEl = (invoice.elements || []).find((el: any) => el.type === 'table');
        const tableRows = tableEl?.rows || [];

        if (Array.isArray(payload.lineItems) && tableRows.length > 0) {
          payload.lineItems = payload.lineItems.map((li: any, idx: number) => {
            const rowVals = tableRows[idx]?.values || {};
            const rawInvItem = (invoice.line_items || [])[idx] || {};

            const merged: Record<string, any> = { ...rowVals, ...rawInvItem, ...li };

            // Dynamically copy any non-empty custom column from rowVals or rawInvItem
            for (const key of Object.keys(rowVals)) {
              if (merged[key] === undefined || merged[key] === null || merged[key] === '') {
                merged[key] = rowVals[key];
              }
            }
            for (const key of Object.keys(rawInvItem)) {
              if (merged[key] === undefined || merged[key] === null || merged[key] === '') {
                merged[key] = rawInvItem[key];
              }
            }

            return merged;
          });
        }

        // ⚡ Forward user's exact QR position (left / center / right) for BOTH English and Bilingual
        const dvAr = invoice.dynamic_values_ar || {};
        const chosenPos = (
          invoice.qr_position ||
          dvAr.qr_position ||
          invoice.qrPosition ||
          invoice.autoQrPosition ||
          'right' // Default to right if set
        ).toLowerCase();

        payload.autoQrPosition = (chosenPos === 'left' || chosenPos === 'center' || chosenPos === 'right') ? chosenPos : 'right';
        payload.qr_position = payload.autoQrPosition;
        payload.autoQrSize = invoice.qr_size || payload.autoQrSize || 130;
        payload.autoQrLabelSize = invoice.qr_label_size || payload.autoQrLabelSize || 11;
        payload.showInvoiceQR = invoice.show_invoice_qr !== false;

        // ⚡ PASS REAL PUBLIC TOKEN TO THE QR RENDERER (Stops POS-REC-xxxxx fallback!)
        const realPublicToken = invoice.public_token || invoice.publicToken || '';
        const realInvoiceUrl = `https://cloudairambo.com/invoice/view/${realPublicToken}`;

        payload.publicToken = realPublicToken;
        payload.public_token = realPublicToken;
        payload.publicUrl = realInvoiceUrl;
        payload.invoiceUrl = realInvoiceUrl;
        payload.invoiceQR = realInvoiceUrl;

        // ⚡ Patch template elements if an invoice QR is inside the canvas
        if (Array.isArray(payload.elements)) {
          payload.elements = payload.elements.map((el: any) => {
            if (el.type === 'qr' && (el._auto === 'invoice' || !el.qrValue || el.qrValue.includes('/invoice/view') || el.qrValue.includes('{invoice'))) {
              return { ...el, qrValue: realInvoiceUrl };
            }
            return el;
          });
        }
        if (payload.template?.elements && Array.isArray(payload.template.elements)) {
          payload.template.elements = payload.template.elements.map((el: any) => {
            if (el.type === 'qr' && (el._auto === 'invoice' || !el.qrValue || el.qrValue.includes('/invoice/view') || el.qrValue.includes('{invoice'))) {
              return { ...el, qrValue: realInvoiceUrl };
            }
            return el;
          });
        }

        // ⚡ Forward Tracking QR & Order Token to the PDF renderer!
        const orderTok = invoice.order_token || invoice.orderToken || dvAr.order_token || payload.orderToken || '';
        payload.orderToken = orderTok;
        payload.order_token = orderTok;
        payload.showTrackingQR = Boolean(orderTok) && invoice.show_tracking_qr !== false;
        payload.show_tracking_qr = payload.showTrackingQR;

        const isBilingual = Boolean(invoice.is_bilingual);
        payload.bilingualMode = isBilingual;
        const targetLang = invoice.translated_lang || invoice.translate_target_lang || dvAr.translate_target_lang || payload.translateTargetLang || 'ar';
        payload.translateTargetLang = targetLang;
        payload.bilingualLayout = invoice.bilingual_layout || 'side';

        let invCap = isBilingual ? (dvAr.qr_caption_invoice || invoice.qr_caption_invoice || payload.qrCaptionInvoice || '') : '';
        let trackCap = isBilingual ? (dvAr.qr_caption_tracking || invoice.qr_caption_tracking || payload.qrCaptionTracking || '') : '';

        // ⚡ 100+ DYNAMIC LANGUAGES (Zero Hardcoding): Auto-translate captions if missing
        if (isBilingual && (!invCap || !trackCap)) {
          try {
            const [fetchedInv, fetchedTrack] = await Promise.all([
              !invCap
                ? fetch(`${BASE}/translate?text=${encodeURIComponent('Scan to view invoice')}&tl=${targetLang}`).then(r => r.json()).then(d => d.translated).catch(() => '')
                : invCap,
              !trackCap
                ? fetch(`${BASE}/translate?text=${encodeURIComponent('Scan to track order')}&tl=${targetLang}`).then(r => r.json()).then(d => d.translated).catch(() => '')
                : trackCap,
            ]);
            if (fetchedInv) invCap = fetchedInv;
            if (fetchedTrack) trackCap = fetchedTrack;
          } catch (err) {
            console.warn('[PrintInvoicePage] Dynamic QR translation failed:', err);
          }
        }

        payload.qrCaptionInvoice = invCap;
        payload.qrCaptionInvoiceAr = invCap;
        payload.translatedQrCaption = invCap;
        payload.qrCaptionTracking = trackCap;
        payload.qrCaptionTrackingAr = trackCap;

        // ⚡ Forward translated captions to ALL places MobileInvoicePreviewContent looks for them
        payload.translatedValues = isBilingual ? {
          ...dvAr,
          ...(payload.translatedValues || {}),
          qr_caption_invoice: invCap,
          qr_caption_tracking: trackCap,
          qrCaptionInvoiceAr: invCap,
          qrCaptionTrackingAr: trackCap,
          translatedQrCaption: invCap,
        } : {};
        payload.invoiceNumber = invoice.invoice_number || payload.invoiceNumber;

        // Pre-translate template "Scan to ..." captions that have no saved translation
        if (isBilingual) {
          const fillCaptions = async (els: any[]) => {
            if (!Array.isArray(els)) return els;
            return Promise.all(els.map(async (el: any) => {
              if (el.type === 'qr' && el.label?.trim() && !el.arabicLabel && !el.labelAr) {
                try {
                  const r = await fetch(`${BASE}/translate?text=${encodeURIComponent(el.label.trim())}&tl=${targetLang}`);
                  const d = await r.json();
                  if (d?.translated) return { ...el, arabicLabel: d.translated };
                } catch {}
              }
              if (el.type === 'text' && /^scan to/i.test((el.textContent || '').trim()) && !el.arabicContent) {
                try {
                  const r = await fetch(`${BASE}/translate?text=${encodeURIComponent(el.textContent.trim())}&tl=${targetLang}`);
                  const d = await r.json();
                  if (d?.translated) return { ...el, arabicContent: d.translated };
                } catch {}
              }
              return el;
            }));
          };
          if (payload.template?.elements) payload.template.elements = await fillCaptions(payload.template.elements);
          if (Array.isArray(payload.savedElements)) payload.savedElements = await fillCaptions(payload.savedElements);
        }

        console.log('[PrintInvoicePage] payload built with QR pos:', payload.autoQrPosition, 'orderToken:', payload.orderToken, 'showTrackingQR:', payload.showTrackingQR);
        setData(payload);
      } catch (e: any) {
        console.log('[PrintInvoicePage] ERROR:', e.message);
        setError(e.message || 'Failed to load invoice');
      }
    })();
  }, [id, token]);

  useEffect(() => {
    if (data) {
      // ⚡ Guarantees Puppeteer captures the page without timing out
      (window as any).__PRINT_READY__ = true;
    }
  }, [data]);

  if (error) return <div style={{ padding: 20, color: 'red' }}>{error}</div>;
  if (!data) return <div style={{ padding: 20 }}>Loading invoice…</div>;

  const isThermal = Boolean(data.isThermal);

  return (
    <div
      id="print-container"
      style={{
        width: isThermal ? '100%' : '794px',
        margin: '0 auto',
        backgroundColor: '#ffffff',
        position: 'relative',
      }}
    >
      <style>{`
        @page {
          size: ${isThermal ? 'auto' : 'A4 portrait'};
          margin: 0;
          margin-top: ${isThermal ? '0' : '14mm'};
          margin-bottom: ${isThermal ? '0' : '12mm'};
        }
        @page :first {
          margin-top: 0;
        }
        html, body, #root, #print-container {
          margin: 0 !important;
          padding: 0 !important;
          width: ${isThermal ? '100%' : '794px'} !important;
          max-width: ${isThermal ? '100%' : '794px'} !important;
          background: #ffffff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          overflow: visible !important;
        }

        /* ⚡ Standard Industry Flow: Never separate QR code from its caption or slice items */
        tr,
        img,
        canvas,
        [data-elid="signature"],
        [data-elid="stamp"],
        [data-elid*="qr"],
        [data-elid*="invoice-qr"],
        [data-elid*="tracking-qr"] {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }

        /* ⚡ Ensure QR and its caption travel together across pages */
        [data-elid="preview-invoice-qr"],
        [data-elid="preview-invoice-qr-label"],
        [data-elid="preview-tracking-qr"],
        [data-elid="preview-tracking-qr-label"] {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }

        /* ⚡ Remove the dashed border WITHOUT hiding the QR container */
        div[style*="dashed"] {
          border-top: none !important;
          border-bottom: none !important;
          border-style: none !important;
          border: none !important;
        }
        hr {
          display: none !important;
        }

        /* ⚡ Clean Natural Height Flow (No forced 1123px locks or artificial jumps) */
        .mobile-invoice-canvas {
          width: ${isThermal ? '100%' : '794px'} !important;
          max-width: ${isThermal ? '100%' : '794px'} !important;
          height: auto !important;
          background: #ffffff !important;
          background-image: none !important;
          overflow: visible !important;
        }
        .mobile-invoice-canvas > div {
          transform: none !important;
          width: ${isThermal ? '100%' : '794px'} !important;
          max-width: ${isThermal ? '100%' : '794px'} !important;
          height: auto !important;
          box-shadow: none !important;
          border: none !important;
          outline: none !important;
        }
        .mobile-invoice-canvas main,
        .mobile-invoice-canvas main > div,
        .mobile-invoice-canvas [data-page] {
          box-shadow: none !important;
          border: none !important;
          outline: none !important;
          background: #ffffff !important;
        }
        .mobile-invoice-canvas > div[style*="scale("] {
          transform: none !important;
          width: ${isThermal ? '100%' : '794px'} !important;
          max-width: ${isThermal ? '100%' : '794px'} !important;
        }
      `}</style>
      <MobileInvoicePreviewContent
        data={data}
        gestureZoom={1}
        setGestureZoom={() => {}}
        pan={{ x: 0, y: 0 }}
        qrOffset={{ x: 0, y: 0 }}
        measuredTableHeight={0}
        canvasRef={{ current: null }}
        handleGestureStart={() => {}}
        handleGestureMove={() => {}}
        handleGestureEnd={() => {}}
        handleResetView={() => {}}
        fitZoomToken={0}
      />
    </div>
  );
}